<template>
  <SbPortal
    ref="portalRef"
    :target="modalTarget"
    :disabled="disabledTargetDefault"
  >
    <SbBlokUi
      v-if="open"
      v-focus-trap="focusTrapConfig"
      class="sb-modal__sb-block-ui"
      :style="computedBlokUiStyle"
      :full-width="fullWidth"
      @mousedown="wrapClose"
    >
      <dialog
        ref="modal"
        class="sb-modal"
        :class="computedClasses"
        :style="computedStyle"
        v-bind="$attrs"
      >
        <SbModalCloseButton
          :class="classes?.closeButton"
          v-if="showClose"
          :data-testid="closeButtonDataTestId"
        />
        <slot />
      </dialog>
    </SbBlokUi>
  </SbPortal>
</template>

<script lang="ts">
import SbBlokUi from '../BlockUI'
import SbPortal from '../Portal'
import { randomString } from '../../utils'
import { FocusTrap } from '../../directives'

import SbModalCloseButton from './components/SbModalCloseButton.vue'
import type { PropType, CSSProperties } from 'vue'

// Type guard to check if the target is a Node
function isNode(target: EventTarget | null): target is Node {
  return target instanceof Node
}

export default {
  name: 'SbModal',

  components: {
    SbPortal,
    SbBlokUi,
    SbModalCloseButton,
  },

  directives: {
    FocusTrap,
  },

  provide() {
    return {
      modalContext: () => this.modalContext,
    }
  },

  props: {
    closeOnHeader: Boolean,
    isOpen: {
      type: Boolean,
      default: false,
    },
    escCloses: {
      type: Boolean,
      default: true,
    },
    fullWidth: Boolean,
    scrollbar: Boolean,
    large: Boolean,
    maxWidth: {
      type: [String, Number],
      default: null,
    },
    modalTarget: {
      type: String,
      default: () => `#sb-modal-target-${randomString(4)}`,
    },
    disabledTargetDefault: Boolean,
    overlayPosition: {
      type: String as PropType<
        'fixed' | 'absolute' | 'sticky' | 'relative' | 'static'
      >,
      default: 'fixed',
    },
    overlayTransparent: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: String,
      default: '',
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    classes: {
      type: Object as PropType<{
        closeButton?: string
      }>,
    },
    hasFocusTrap: {
      type: Boolean,
      default: true,
    },
    enableInitialFocus: {
      type: Boolean,
      default: true,
    },
    escapeBlockingSelectors: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },

  emits: ['hide', 'show'],

  data() {
    return {
      open: this.isOpen,
    }
  },

  computed: {
    closeButtonDataTestId() {
      const dataTestid = this.$attrs['data-testid']
      return dataTestid ? `${dataTestid}-close-button` : 'sb-modal-close-button'
    },

    computedClasses() {
      return [
        this.customClass?.length && this.customClass,
        this.fullWidth && 'sb-modal__full-width',
        this.large && 'sb-modal__large',
        this.scrollbar && 'sb-modal--scrollbar',
        this.closeOnHeader && 'sb-modal--close-on-header',
      ]
    },

    computedBlokUiStyle() {
      const style: CSSProperties = { position: this.overlayPosition ?? 'fixed' }

      if (this.overlayTransparent) style.background = 'none'

      return { ...style }
    },

    computedStyle() {
      if (!this.maxWidth) return null

      return { maxWidth: `${this.maxWidth}px` }
    },

    modalContext() {
      return {
        closeModal: this.handleCloseModal,
      }
    },

    focusTrapConfig() {
      return {
        enabled: this.hasFocusTrap && this.open,
        enableInitialFocus: this.enableInitialFocus,
      }
    },
  },

  watch: {
    isOpen(value) {
      if (value) {
        this.handleOpenModal()
      } else {
        this.handleCloseModal()
      }
    },
  },

  created() {
    this.$_createPortalInstance()
    if (this.isOpen) {
      this.handleCloseModalByPressingEsc()
    }
  },

  beforeUnmount() {
    if (document.querySelector) {
      const body = document.querySelector('body')
      if (body) {
        body.style.overflow = 'auto'
      }
    }
  },

  methods: {
    escapeEventListener(e: KeyboardEvent) {
      if (e.key === 'Escape' && this.escCloses) {
        for (const selector of this.escapeBlockingSelectors) {
          const elements = document.querySelectorAll(selector)
          for (const element of elements) {
            const style = window.getComputedStyle(element)
            if (
              style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              style.opacity !== '0'
            ) {
              return
            }
          }
        }

        this.handleCloseModal()
      }
    },

    removeListener() {
      window.removeEventListener('keydown', this.escapeEventListener, true)
    },

    registerListener() {
      window.addEventListener('keydown', this.escapeEventListener, true)
    },
    /**
     * handler for closing the modal by pressing ESC on the keyboard
     */
    handleCloseModalByPressingEsc() {
      if (this.escCloses) {
        this.registerListener()
      }
    },
    /**
     * handler for close modal
     */
    handleCloseModal() {
      if (this.open) {
        this.removeListener()
        this.open = false
        this.$nextTick(() => {
          const body = document.querySelector('body')
          if (body) {
            body.style.overflow = 'auto'
          }
          this.$emit('hide')
        })
      }
    },

    /**
     * handler for open modal
     */
    handleOpenModal() {
      if (!this.open) {
        this.handleCloseModalByPressingEsc()
        this.open = true
        this.$nextTick(() => {
          const body = document.querySelector('body')
          if (body) {
            body.style.overflow = 'hidden'
          }
          this.$emit('show')
        })
      }
    },

    /**
     * this function allows to hide the overflow
     */
    hideOverflow() {
      const body = document.querySelector('body')
      if (body) {
        body.style.overflow = 'hidden'
      }
    },

    wrapClose(event: MouseEvent) {
      const modal = this.$refs.modal as HTMLDialogElement

      if (
        modal &&
        isNode(event.target) &&
        !modal.contains(event.target) &&
        this.closeOnClickOutside
      ) {
        this.handleCloseModal()
      }
    },

    /**
     * function to create the portal instance
     */
    $_createPortalInstance() {
      // TODO: This needs to be refactored to remove typeassertion when moved to composition API
      const portalRef = this.$refs.portalRef as
        | { mountTarget?: () => void }
        | undefined
      if (portalRef && typeof portalRef.mountTarget === 'function') {
        portalRef.mountTarget()
      }
    },
  },
}
</script>
